import { useCallback, useEffect, useState } from "react";
import { suppliersApi, type SupplierView } from "../api/assets";
import type { ApiError } from "../api/client";

/**
 * The supplier codebook (`GET /suppliers`, sprint 5h) shared by every screen that picks one —
 * the asset form, the asset card — and by the codebook's own list. Same shape as
 * `useManufacturers`: fetched once per session, cached at module level; a write calls
 * `refresh()` so every open picker sees it.
 */

let cache: SupplierView[] | null = null;
let inflight: Promise<SupplierView[]> | null = null;
const listeners = new Set<() => void>();

function load(force = false): Promise<SupplierView[]> {
  if (!force && cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = suppliersApi
    .list()
    .then((list) => {
      cache = list;
      inflight = null;
      for (const l of listeners) l();
      return list;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

/** Re-fetch the codebook after a write made outside a mounted hook (the supplier's own card). */
export function refreshSuppliers(): Promise<SupplierView[]> {
  return load(true);
}

/** Test hook: drop the module cache so the next mount fetches again. */
export function resetSuppliersCache(): void {
  cache = null;
  inflight = null;
}

export type SuppliersHook = {
  /** Sorted by name (as the server returns them). Empty while loading or on error. */
  suppliers: SupplierView[];
  loading: boolean;
  error: string | null;
  /** Re-fetch after a write; every mounted hook re-renders. */
  refresh: () => Promise<SupplierView[]>;
};

export function useSuppliers(): SuppliersHook {
  const [suppliers, setSuppliers] = useState<SupplierView[]>(cache ?? []);
  const [loading, setLoading] = useState(cache === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const sync = () => { if (!cancelled && cache) setSuppliers(cache); };
    listeners.add(sync);
    load()
      .then(() => { if (!cancelled) { sync(); setLoading(false); } })
      .catch((err: ApiError) => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; listeners.delete(sync); };
  }, []);

  const refresh = useCallback(() => load(true), []);
  return { suppliers, loading, error, refresh };
}
